// settings section for the sync-server snapshot system (server-side backups).
// lists the open budget's snapshots, takes a manual one and restores one.
// restore swaps the server file onto a new sync id, so afterwards the local
// copy is stale | we close + re-download the budget right away.
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { closeAndDownloadBudget } from '#budgetfiles/budgetfilesSlice';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useDispatch } from '#redux';

import { Setting } from './UI';

type BackupSnapshot = {
  id: string;
  createdAt: string;
  reason: string;
  bytes: number;
};

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export function ServerBackups() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [cloudFileId] = useMetadataPref('cloudFileId');

  const [snapshots, setSnapshots] = useState<BackupSnapshot[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  // restore is two-click | first press arms the row, second one runs it
  const [armedId, setArmedId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // labels for the snapshot reasons the server writes
  const reasonLabel: Record<string, string> = {
    manual: t('Manual'),
    scheduled: t('Scheduled'),
    'pre-banksync': t('Before bank sync'),
    'pre-migration': t('Before migration'),
    'pre-restore': t('Before restore'),
  };

  const refresh = useCallback(async () => {
    const res = await send('backups-list');
    if ('error' in res) {
      setError(res.error);
    } else {
      setError('');
      setEnabled(res.enabled);
      setSnapshots(res.snapshots);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (cloudFileId) void refresh();
  }, [cloudFileId, refresh]);

  // server backups only exist for budgets that live on a server
  if (!cloudFileId || (loaded && error === 'no-cloud-file')) {
    return null;
  }

  async function onCreate() {
    setCreating(true);
    const res = await send('backups-create');
    if ('error' in res) {
      setError(res.error);
    } else {
      await refresh();
    }
    setCreating(false);
  }

  async function onRestore(snapshotId: string) {
    if (armedId !== snapshotId) {
      setArmedId(snapshotId);
      return;
    }
    setArmedId(null);
    setRestoringId(snapshotId);
    const res = await send('backups-restore', { snapshotId });
    setRestoringId(null);
    if ('error' in res) {
      setError(res.error);
      return;
    }
    // the server file now points at a new sync id | replace the local copy
    await dispatch(
      closeAndDownloadBudget({ cloudFileId: String(cloudFileId) }),
    );
  }

  return (
    <Setting
      primaryAction={
        <ButtonWithLoading isLoading={creating} onPress={onCreate}>
          <Trans>Back up now</Trans>
        </ButtonWithLoading>
      }
    >
      <Text>
        <Trans>
          <strong>Server backups</strong> are snapshots of this budget stored on
          your sync server. They are taken automatically before migrations and
          bank syncs; you can also take one manually. Restoring replaces the
          current budget with the snapshot and re-downloads it on this device.
        </Trans>
      </Text>
      {!enabled && loaded && !error && (
        <Text style={{ color: theme.warningText }}>
          <Trans>
            Automatic backups are disabled on the server. Manual backups still
            work.
          </Trans>
        </Text>
      )}
      {error && error !== 'no-cloud-file' && (
        <Text style={{ color: theme.errorText }}>
          <Trans>Backup error: {{ error }}</Trans>
        </Text>
      )}
      {loaded && !error && snapshots.length === 0 && (
        <Text style={{ color: theme.pageTextSubdued }}>
          <Trans>No snapshots yet.</Trans>
        </Text>
      )}
      {snapshots.length > 0 && (
        <View style={{ gap: 6 }}>
          {snapshots.map(snap => (
            <View
              key={snap.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text data-vrt-mask>
                  {new Date(snap.createdAt).toLocaleString()}
                </Text>
                <Text
                  style={{ color: theme.pageTextSubdued, fontSize: 12 }}
                  data-vrt-mask
                >
                  {reasonLabel[snap.reason] ?? snap.reason} ·{' '}
                  {formatBytes(snap.bytes)}
                </Text>
              </View>
              <Button
                variant={armedId === snap.id ? 'primary' : 'normal'}
                isDisabled={restoringId !== null}
                onPress={() => onRestore(snap.id)}
              >
                {restoringId === snap.id ? (
                  <Trans>Restoring…</Trans>
                ) : armedId === snap.id ? (
                  <Trans>Really restore?</Trans>
                ) : (
                  <Trans>Restore</Trans>
                )}
              </Button>
            </View>
          ))}
        </View>
      )}
    </Setting>
  );
}
